import type { ExerciseTemplate } from '@yuan-math/exercises';
import { div, fraction, gcd, lcm, mul, rawFraction, roundTo, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  expressionExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Grade 6 exercises.
 *
 * Each template's traps encode the specific way this year's mathematics goes
 * wrong: the wrong fraction inverted, a part-to-part ratio read as
 * part-to-whole, a percent used as a whole number, negatives ordered by
 * magnitude, an exponent multiplied, a triangle left unhalved.
 */
/**
 * Cofactor pairs that share nothing, so that `shared * m` and `shared * n`
 * have a greatest common factor of exactly `shared`. Building the numbers this
 * way means the answer is known before the question is drawn, and no template
 * has to compute a GCF it might get wrong.
 */
const COPRIME_PAIRS: readonly [number, number][] = [
  [2, 3], [2, 5], [2, 7], [2, 9],
  [3, 4], [3, 5], [3, 7], [3, 8],
  [4, 5], [4, 7], [4, 9],
  [5, 6], [5, 7], [5, 8], [5, 9],
  [6, 7], [7, 8], [7, 9], [8, 9],
];

/** The largest factor of a number other than itself; 1 when the number is prime. */
function largestProperDivisor(value: number): number {
  for (let factor = 2; factor <= value; factor += 1) {
    if (value % factor === 0) return value / factor;
  }
  return 1;
}

/**
 * The four inequality symbols, kept in one place because every template here
 * offers all four as options: the whole diagnostic value of these questions is
 * in which of the other three a student reaches for.
 */
const SYMBOLS = ['<', '\\le', '>', '\\ge'] as const;

/** True when both symbols point the same way, differing only in strictness. */
const sameDirection = (a: string, b: string): boolean =>
  (a === '<' || a === '\\le') === (b === '<' || b === '\\le');

const symbolIncludes = (symbol: string): boolean => symbol === '\\le' || symbol === '\\ge';

/** The strict version of a symbol — what is left once strictness is stripped. */
const strictOf = (symbol: string): string => (symbol === '<' || symbol === '\\le' ? '<' : '>');

const symbolText = (symbol: string): string =>
  symbol === '\\le' ? '≤' : symbol === '\\ge' ? '≥' : symbol;

const describeSymbol = (symbol: string): string =>
  symbol === '\\le'
    ? 'Wording like "at most" or "no more than" allows the boundary value'
    : symbol === '\\ge'
      ? 'Wording like "at least" allows the boundary value'
      : symbol === '<'
        ? 'Wording like "under" leaves the boundary value out'
        : 'Wording like "more than" leaves the boundary value out';

export const MIDDLE_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------ divide fractions */
  template({
    id: 'frac-divide',
    conceptId: 'divide-fractions',
    description: 'Divide two fractions using the reciprocal.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 5 : difficulty === 3 ? 8 : 10;
      const d1 = rng.int(2, ceiling);
      const d2 = rng.int(2, ceiling);
      const n1 = rng.int(1, d1 - 1 || 1);
      const n2 = rng.int(1, d2 - 1 || 1);
      const a = rawFraction(n1, d1);
      const b = rawFraction(n2, d2);
      const answer = div(a, b);
      return fractionExercise({
        prompt: 'Divide. Give your answer in lowest terms.',
        latex: `${toLatex(a)} \\div ${toLatex(b)}`,
        value: answer,
        requireSimplified: true,
        summary: `Keep ${toText(a)}, flip ${toText(b)} to ${n2 === 0 ? '' : `${d2}/${n2}`}, then multiply.`,
        hint: 'Keep the first fraction, flip the second, change the sign to multiply.',
        steps: [
          { label: 'Keep the first fraction; take the reciprocal of the second.', latex: `${toLatex(a)} \\times \\frac{${d2}}{${n2}}` },
          { label: 'Multiply across.', latex: toLatex(rawFraction(n1 * d2, d1 * n2)) },
          { label: 'Simplify.', latex: toLatex(answer) },
        ],
        traps: [
          // Flipping the dividend instead of the divisor.
          fractionTrap(div(rawFraction(d1, n1), rawFraction(d2, n2)), 'invert-first-fraction'),
          fractionTrap(mul(a, b), 'cross-multiply-to-add'),
        ],
      });
    },
  }),

  template({
    id: 'frac-divide-howmany',
    conceptId: 'divide-fractions',
    description: 'How many unit fractions fit into a whole number.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty === 1 ? 4 : 8);
      const whole = rng.int(2, difficulty === 1 ? 6 : 12);
      return numericExercise({
        prompt: `How many pieces of size 1/${d} fit into ${whole}?`,
        latex: `${whole} \\div \\frac{1}{${d}}`,
        value: whole * d,
        summary: `Each whole holds ${d} of them, so ${whole} wholes hold ${whole * d}.`,
        hint: `How many ${d}ths are there in one whole?`,
        steps: [
          { label: 'Count them in one whole.', latex: String(d) },
          { label: `Multiply by the number of wholes.`, latex: `${d} \\times ${whole} = ${whole * d}` },
        ],
        traps: [trap(roundTo(whole / d, 4), 'invert-first-fraction')],
      });
    },
  }),

  /* ----------------------------------------------------------- integers -- */
  template({
    id: 'integer-arithmetic',
    conceptId: 'integers',
    description: 'Add or subtract with negative numbers.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 12 : difficulty === 3 ? 25 : 60;
      const a = rng.int(-ceiling, ceiling);
      const b = rng.int(1, ceiling);
      const subtractNegative = difficulty >= 3 && rng.bool();

      if (subtractNegative) {
        return numericExercise({
          prompt: 'Compute.',
          latex: `${a} - (-${b})`,
          value: a + b,
          summary: `Subtracting a negative adds: ${a} + ${b} = ${a + b}.`,
          hint: 'Taking away a debt leaves you better off.',
          steps: [
            { label: 'Subtracting a negative is adding.', latex: `${a} + ${b}` },
            { label: 'Compute.', latex: String(a + b) },
          ],
          traps: [trap(a - b, 'subtracting-negative')],
        });
      }

      return numericExercise({
        prompt: 'Compute.',
        latex: `${a} + (-${b})`,
        value: a - b,
        summary: `Adding a negative moves left: ${a} − ${b} = ${a - b}.`,
        hint: 'Adding a negative moves you left along the number line.',
        steps: [
          { label: 'Adding a negative is subtracting.', latex: `${a} - ${b}` },
          { label: 'Compute.', latex: String(a - b) },
        ],
        traps: [trap(a + b, 'subtracting-negative')],
      });
    },
  }),

  template({
    id: 'integer-compare',
    conceptId: 'integers',
    description: 'Compare two negative numbers.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const a = -rng.int(1, 20);
      let b = -rng.int(1, 20);
      if (b === a) b = a - 1;
      const larger = Math.max(a, b);
      const { choices, correctId } = options(
        [
          { label: String(a), correct: a === larger, ...(a !== larger ? { misconceptionId: 'negative-magnitude-order' } : {}) },
          { label: String(b), correct: b === larger, ...(b !== larger ? { misconceptionId: 'negative-magnitude-order' } : {}) },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which number is greater?',
        figure: { kind: 'number-line', from: Math.min(a, b) - 2, to: 2, step: 2 },
        choices,
        correctId,
        summary: `${larger} sits further right on the number line, so it is greater.`,
        hint: 'Further right on the number line is always greater.',
        steps: [
          { label: 'Picture both on the number line.', latex: `${Math.min(a, b)} \; \\text{and} \; ${larger}` },
          { label: 'The one further right is greater.', latex: `${larger} > ${Math.min(a, b)}` },
        ],
      });
    },
  }),

  /* ---------------------------------------------------------- exponents -- */
  template({
    id: 'exponent-evaluate',
    conceptId: 'exponents',
    description: 'Evaluate a power.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const base = rng.int(2, difficulty === 1 ? 5 : difficulty === 2 ? 9 : 12);
      const exponent = difficulty === 1 ? 2 : rng.int(2, difficulty === 3 ? 4 : 3);
      const value = base ** exponent;
      return numericExercise({
        prompt: 'Evaluate.',
        latex: `${base}^{${exponent}}`,
        value,
        summary: `The exponent counts the copies: ${base}^${exponent} means ${Array.from({ length: exponent }, () => base).join(' × ')}, which is ${value}.`,
        hint: `Multiply ${base} by itself ${exponent} times — the exponent counts the copies.`,
        steps: [
          { label: 'Write out the repeated multiplication.', latex: Array.from({ length: exponent }, () => base).join(' \\times ') },
          { label: 'Compute.', latex: String(value) },
        ],
        traps: [trap(base * exponent, 'exponent-as-product')],
      });
    },
  }),

  template({
    id: 'exponent-in-expression',
    conceptId: 'exponents',
    description: 'Evaluate an expression containing a power.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const base = rng.int(2, 5);
      const exponent = rng.int(2, 3);
      const coefficient = rng.int(2, 6);
      const constant = rng.int(1, difficulty === 4 ? 20 : 9);
      const value = constant + coefficient * base ** exponent;
      return numericExercise({
        prompt: 'Evaluate.',
        latex: `${constant} + ${coefficient} \\times ${base}^{${exponent}}`,
        value,
        summary: `Exponent first: ${base}^${exponent} = ${base ** exponent}, then multiply, then add.`,
        hint: 'Brackets, exponents, multiply and divide, add and subtract.',
        steps: [
          { label: 'Exponents come before multiplication.', latex: `${base}^{${exponent}} = ${base ** exponent}` },
          { label: 'Multiply.', latex: `${coefficient} \\times ${base ** exponent} = ${coefficient * base ** exponent}` },
          { label: 'Add.', latex: String(value) },
        ],
        traps: [
          trap(constant + coefficient * base * exponent, 'exponent-as-product'),
          trap((constant + coefficient) * base ** exponent, 'multiply-then-add-wrong-order'),
        ],
      });
    },
  }),

  /* -------------------------------------------------- prime factorisation */
  template({
    id: 'prime-identify',
    conceptId: 'prime-factorization',
    description: 'Decide whether a number is prime.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const primes = difficulty === 1 ? [2, 3, 5, 7, 11, 13] : [17, 19, 23, 29, 31, 37, 41, 43];
      const composites = difficulty === 1 ? [4, 6, 8, 9, 10, 12, 15] : [21, 25, 27, 33, 35, 39, 49, 51];
      const isPrime = rng.bool();
      const number = isPrime ? rng.pick(primes) : rng.pick(composites);
      const factor = isPrime ? 0 : [2, 3, 5, 7].find((f) => number % f === 0) ?? 0;
      const { choices, correctId } = options(
        [
          { label: 'Prime', correct: isPrime },
          { label: 'Composite', correct: !isPrime },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Is ${number} prime or composite?`,
        choices,
        correctId,
        summary: isPrime
          ? `${number} has no factors except 1 and itself, so it is prime.`
          : `${number} = ${factor} × ${number / factor}, so it is composite.`,
        hint: 'Try dividing by 2, then 3, then 5, then 7.',
        steps: [
          { label: 'Test the small primes in turn.', latex: '2, \; 3, \; 5, \; 7' },
          {
            label: isPrime ? 'None of them divides it exactly.' : 'One of them divides it exactly.',
            latex: isPrime ? `${number} \\text{ is prime}` : `${number} = ${factor} \\times ${number / factor}`,
          },
        ],
      });
    },
  }),

  template({
    id: 'prime-factor-count',
    conceptId: 'prime-factorization',
    description: 'Count the prime factors of a number, with repeats.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const pool = difficulty === 2 ? [12, 18, 20, 28, 45] : difficulty === 3 ? [60, 72, 84, 90, 126] : [180, 252, 300, 360, 504];
      const number = rng.pick(pool);

      const factors: number[] = [];
      let remaining = number;
      for (let p = 2; p <= remaining; p += 1) {
        while (remaining % p === 0) {
          factors.push(p);
          remaining /= p;
        }
      }

      const largest = Math.max(...factors);
      const askLargest = rng.bool();
      return numericExercise({
        prompt: askLargest
          ? `What is the largest prime factor of ${number}?`
          : `How many prime factors does ${number} have, counting repeats?`,
        value: askLargest ? largest : factors.length,
        summary: `${number} = ${factors.join(' × ')}.`,
        hint: 'Divide by 2 as long as you can, then 3, then 5, and so on.',
        steps: [
          { label: 'Break it down into primes.', latex: `${number} = ${factors.join(' \\times ')}` },
          {
            label: askLargest ? 'Take the largest.' : 'Count them, including repeats.',
            latex: String(askLargest ? largest : factors.length),
          },
        ],
        traps: askLargest ? [] : [trap(new Set(factors).size, 'ignored-remainder')],
      });
    },
  }),

  /* --------------------------------------------- algebraic expressions -- */
  template({
    id: 'combine-like-terms',
    conceptId: 'algebraic-expressions',
    description: 'Simplify by combining like terms.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(1, 9);
      const d = rng.int(1, c);
      const letter = rng.pick(['x', 'n', 'y']);

      if (difficulty === 2) {
        return expressionExercise({
          prompt: 'Simplify by combining like terms.',
          latex: `${a}${letter} + ${b}${letter}`,
          canonical: `${a + b}*${letter}`,
          summary: `${a} lots of ${letter} plus ${b} lots of ${letter} is ${a + b} lots of ${letter}.`,
          hint: 'Both terms contain the same letter, so they can be added.',
          steps: [
            { label: 'Add the coefficients.', latex: `(${a} + ${b})${letter}` },
            { label: 'Simplify.', latex: `${a + b}${letter}` },
          ],
        });
      }

      return expressionExercise({
        prompt: 'Simplify by combining like terms.',
        latex: `${a}${letter} + ${c} + ${b}${letter} - ${d}`,
        canonical: `${a + b}*${letter} + ${c - d}`,
        summary: `Combine the ${letter} terms and the constants separately: ${a + b}${letter} + ${c - d}.`,
        hint: 'Group the terms with a letter, then group the plain numbers.',
        steps: [
          { label: 'Group like terms.', latex: `(${a}${letter} + ${b}${letter}) + (${c} - ${d})` },
          { label: 'Combine the variable terms.', latex: `${a + b}${letter}` },
          { label: 'Combine the constants.', latex: String(c - d) },
          { label: 'Answer.', latex: `${a + b}${letter} + ${c - d}` },
        ],
      });
    },
  }),

  template({
    id: 'write-expression-middle',
    conceptId: 'algebraic-expressions',
    description: 'Translate a description into an expression.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rate = rng.int(3, 12);
      const fee = rng.int(2, 15);
      const scenario = rng.pick([
        { text: `A cinema charges £${rate} per ticket plus a £${fee} booking fee for the order. Write an expression for the cost of n tickets.`, canonical: `${rate}*n + ${fee}` },
        { text: `A plumber charges £${rate} per hour plus a £${fee} call-out fee. Write an expression for the cost of n hours.`, canonical: `${rate}*n + ${fee}` },
        { text: `A crate holds ${rate} apples. Write an expression for the number of apples in n crates plus ${fee} loose ones.`, canonical: `${rate}*n + ${fee}` },
      ]);
      return expressionExercise({
        prompt: scenario.text,
        canonical: scenario.canonical,
        summary: `Each unit contributes ${rate}, so that is ${rate}n, and ${fee} is added just once.`,
        hint: 'Which part changes with n, and which part is charged only once?',
        steps: [
          { label: 'The part that depends on n.', latex: `${rate}n` },
          { label: 'The part that does not.', latex: String(fee) },
          { label: 'Put them together.', latex: `${rate}n + ${fee}` },
        ],
      });
    },
  }),

  /* --------------------------------------------------- decimal operations */
  template({
    id: 'decimal-multiply',
    conceptId: 'decimal-operations',
    description: 'Multiply two decimals, counting decimal places.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      // Trailing zeros in a factor would print "2.0", which nobody writes.
      const nonTerminal = (value: number): number => (value % 10 === 0 ? value + rng.int(1, 9) : value);
      const aDigits = nonTerminal(difficulty === 4 ? rng.int(105, 985) : rng.int(12, 98));
      const bDigits = difficulty === 2 ? rng.int(2, 9) : nonTerminal(rng.int(12, 48));
      const aPlaces = difficulty === 4 ? 2 : 1;
      const a = aDigits / 10 ** aPlaces;
      const b = bDigits / 10;
      const places = aPlaces + 1;
      const answer = roundTo((aDigits * bDigits) / 10 ** places, 6);

      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a.toFixed(aPlaces)} \\times ${b.toFixed(1)}`,
        value: answer,
        summary: `${aDigits} × ${bDigits} = ${aDigits * bDigits}, and the question has ${places} decimal places, so the answer does too.`,
        hint: 'Multiply as if the points were not there, then count the decimal places in the question.',
        steps: [
          { label: 'Multiply the digits, ignoring both points.', latex: `${aDigits} \\times ${bDigits} = ${aDigits * bDigits}` },
          { label: 'Count the decimal places in the question.', latex: `${aPlaces} + 1 = ${places}` },
          { label: 'Put that many decimal places into the answer.', latex: String(answer) },
        ],
        traps: [
          // One place too few, and one too many: the same failure to count.
          trap(roundTo(answer * 10, 6), 'decimal-places-not-counted'),
          trap(roundTo(answer / 10, 6), 'decimal-places-not-counted'),
        ],
      });
    },
  }),

  template({
    id: 'decimal-divide',
    conceptId: 'decimal-operations',
    description: 'Divide by a decimal by shifting both numbers.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const quotient = difficulty === 2 ? rng.int(2, 9) : rng.int(3, 18);
      // At difficulty 4 the divisor has two decimal places, so both numbers
      // have to shift twice rather than once.
      const twoPlaces = difficulty === 4;
      const divisorDigits = twoPlaces ? rng.pick([15, 25, 35, 45, 75]) : rng.int(2, 9);
      const scale = twoPlaces ? 100 : 10;
      const divisor = divisorDigits / scale;
      const dividend = roundTo((quotient * divisorDigits) / scale, 6);
      const divisorPlaces = twoPlaces ? 2 : 1;

      const shiftTrap = roundTo(quotient / scale, 6);
      const reciprocalTrap = roundTo(1 / quotient, 6);

      return numericExercise({
        prompt: 'Divide.',
        latex: `${dividend} \\div ${divisor.toFixed(divisorPlaces)}`,
        value: quotient,
        summary: `Shifting both numbers by ${scale} gives ${quotient * divisorDigits} ÷ ${divisorDigits} = ${quotient}.`,
        hint: 'Move the point in the divisor until it is a whole number — and move it the same way in the other number.',
        steps: [
          { label: `Multiply both numbers by ${scale}.`, latex: `${dividend} \\div ${divisor.toFixed(divisorPlaces)} = ${quotient * divisorDigits} \\div ${divisorDigits}` },
          { label: 'Now divide whole numbers.', latex: `${quotient * divisorDigits} \\div ${divisorDigits} = ${quotient}` },
          { label: `Check: the divisor is below 1, so the answer is bigger than ${dividend}.`, latex: `${quotient} > ${dividend}` },
        ],
        traps: [
          trap(shiftTrap, 'decimal-divisor-not-shifted'),
          ...(reciprocalTrap !== shiftTrap ? [trap(reciprocalTrap, 'divide-wrong-direction')] : []),
        ],
      });
    },
  }),

  template({
    id: 'decimal-money',
    conceptId: 'decimal-operations',
    description: 'Decimal multiplication and division in a money or measurement context.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const priceDigits = rng.int(115, 975);
      const price = priceDigits / 100;
      const count = rng.int(3, difficulty === 3 ? 8 : 14);
      const total = roundTo((priceDigits * count) / 100, 6);
      const dividing = difficulty === 4 && rng.bool();

      if (dividing) {
        return numericExercise({
          prompt: `${count} identical tickets cost ${total.toFixed(2)} in total. What does one ticket cost?`,
          unit: '£',
          value: roundTo(price, 6),
          summary: `${total.toFixed(2)} ÷ ${count} = ${price.toFixed(2)}.`,
          hint: 'The total is shared between every ticket, so this is a division.',
          steps: [
            { label: 'Divide the total by the number of tickets.', latex: `${total.toFixed(2)} \\div ${count}` },
            { label: 'The point stays in line with the digits.', latex: price.toFixed(2) },
          ],
          traps: [trap(roundTo(count / total, 6), 'divide-wrong-direction')],
        });
      }

      return numericExercise({
        prompt: `One ticket costs ${price.toFixed(2)}. What do ${count} tickets cost?`,
        unit: '£',
        value: total,
        summary: `${priceDigits} × ${count} = ${priceDigits * count}, and two decimal places gives ${total.toFixed(2)}.`,
        hint: 'Multiply the digits, then put the point back two places from the right.',
        steps: [
          { label: 'Multiply, ignoring the point.', latex: `${priceDigits} \\times ${count} = ${priceDigits * count}` },
          { label: 'The price has two decimal places, so the answer does too.', latex: total.toFixed(2) },
        ],
        traps: [
          trap(roundTo(total * 10, 6), 'decimal-places-not-counted'),
          trap(roundTo(total / 10, 6), 'decimal-places-not-counted'),
        ],
      });
    },
  }),

  /* ------------------------------------------------------------ GCF & LCM */
  template({
    id: 'gcf-find',
    conceptId: 'gcf-lcm',
    description: 'Find the greatest common factor of two numbers.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const shared = rng.pick(difficulty === 2 ? [4, 6, 8, 9] : difficulty === 3 ? [6, 8, 9, 10, 12] : [12, 14, 15, 16, 18]);
      const [m, n] = rng.pick(COPRIME_PAIRS);
      const a = shared * m;
      const b = shared * n;
      const lower = largestProperDivisor(shared);

      return numericExercise({
        prompt: `What is the greatest common factor of ${a} and ${b}?`,
        value: shared,
        summary: `${a} = ${shared} × ${m} and ${b} = ${shared} × ${n}, and ${m} and ${n} share nothing, so ${shared} is as large as a common factor can be.`,
        hint: 'A factor cannot be bigger than the smaller of the two numbers.',
        steps: [
          { label: 'Write each number as the shared part times what is left.', latex: `${a} = ${shared} \\times ${m}, \\quad ${b} = ${shared} \\times ${n}` },
          { label: `Since ${m} and ${n} have no common factor, nothing more can be taken out.`, latex: `\\gcd(${a}, ${b}) = ${shared}` },
        ],
        traps: [
          trap(lcm(a, b), 'gcf-lcm-swapped'),
          trap(lower, 'common-factor-not-greatest'),
        ],
      });
    },
  }),

  template({
    id: 'lcm-find',
    conceptId: 'gcf-lcm',
    description: 'Find the lowest common multiple of two numbers.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const shared = rng.pick(difficulty === 2 ? [2, 3, 4] : difficulty === 3 ? [4, 5, 6] : [6, 8, 9, 12]);
      const [m, n] = rng.pick(COPRIME_PAIRS);
      const a = shared * m;
      const b = shared * n;
      const answer = shared * m * n;

      return numericExercise({
        prompt: `What is the lowest common multiple of ${a} and ${b}?`,
        value: answer,
        summary: `${answer} is the first number both ${a} and ${b} divide into: ${answer} ÷ ${a} = ${n} and ${answer} ÷ ${b} = ${m}.`,
        hint: 'A multiple cannot be smaller than the larger of the two numbers.',
        steps: [
          { label: 'They already share this much.', latex: `\\gcd(${a}, ${b}) = ${shared}` },
          { label: 'So count the shared part once, not twice.', latex: `${shared} \\times ${m} \\times ${n} = ${answer}` },
          { label: 'Check that both divide it.', latex: `${answer} \\div ${a} = ${n}, \\quad ${answer} \\div ${b} = ${m}` },
        ],
        traps: [
          trap(shared, 'gcf-lcm-swapped'),
          trap(a * b, 'multiple-not-lowest'),
        ],
      });
    },
  }),

  template({
    id: 'gcf-lcm-decide',
    conceptId: 'gcf-lcm',
    description: 'Decide whether a situation asks for the GCF or the LCM.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const shared = rng.pick([4, 6, 8, 9, 12]);
      const [m, n] = rng.pick(COPRIME_PAIRS);
      const a = shared * m;
      const b = shared * n;
      const multiple = shared * m * n;
      const wantsFactor = rng.bool();

      const story = wantsFactor
        ? rng.pick([
            `A shop has ${a} pens and ${b} pencils, and wants to make identical packs with nothing left over. What is the largest number of packs?`,
            `Two ribbons are ${a} cm and ${b} cm long. They are cut into equal pieces, as long as possible, with none wasted. How long is each piece?`,
          ])
        : rng.pick([
            `One bus leaves every ${a} minutes and another every ${b} minutes. They have just left together. How many minutes until they leave together again?`,
            `Tiles are ${a} cm by ${b} cm. What is the side of the smallest square that can be tiled with them, in centimetres?`,
          ]);

      const { choices, correctId } = options(
        [
          { label: String(shared), correct: wantsFactor, ...(wantsFactor ? {} : { misconceptionId: 'gcf-lcm-swapped' }) },
          { label: String(multiple), correct: !wantsFactor, ...(wantsFactor ? { misconceptionId: 'gcf-lcm-swapped' } : {}) },
          { label: String(a * b), misconceptionId: 'multiple-not-lowest' },
          { label: String(largestProperDivisor(shared)), misconceptionId: 'common-factor-not-greatest' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: story,
        choices,
        correctId,
        summary: wantsFactor
          ? `Equal groups with nothing left over means a common factor, and "largest" means the greatest: ${shared}.`
          : `Coinciding again means a common multiple, and "next" means the lowest: ${multiple}.`,
        hint: 'Splitting things up asks for a factor. Things happening together again asks for a multiple.',
        steps: [
          { label: 'Decide which question is being asked.', latex: wantsFactor ? '\\gcd' : '\\operatorname{lcm}' },
          { label: 'Compute it.', latex: `${wantsFactor ? '\\gcd' : '\\operatorname{lcm}'}(${a}, ${b}) = ${wantsFactor ? shared : multiple}` },
        ],
      });
    },
  }),

  /* ------------------------------------------ opposites and absolute value */
  template({
    id: 'absolute-value-evaluate',
    conceptId: 'absolute-value-opposites',
    description: 'Evaluate an absolute value expression.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const a = -rng.int(2, difficulty === 1 ? 20 : 40);

      if (difficulty === 1) {
        return numericExercise({
          prompt: 'Evaluate.',
          latex: `|${a}|`,
          value: -a,
          summary: `${a} is ${-a} away from zero, and a distance is never negative.`,
          hint: 'How far is it from zero?',
          steps: [{ label: 'Distance from zero, ignoring the direction.', latex: `|${a}| = ${-a}` }],
          traps: [trap(a, 'absolute-value-keeps-sign')],
        });
      }

      const b = rng.bool() ? rng.int(2, 30) : -rng.int(2, 30);
      const answer = -a + Math.abs(b);
      return numericExercise({
        prompt: 'Evaluate.',
        latex: `|${a}| + |${b}|`,
        value: answer,
        summary: `${-a} + ${Math.abs(b)} = ${answer}. Both bars measure a distance, so both parts are positive.`,
        hint: 'Take each absolute value first, then add.',
        steps: [
          { label: 'Each is a distance from zero.', latex: `|${a}| = ${-a}, \\quad |${b}| = ${Math.abs(b)}` },
          { label: 'Now add.', latex: `${-a} + ${Math.abs(b)} = ${answer}` },
        ],
        traps: [trap(a + b, 'absolute-value-keeps-sign')],
      });
    },
  }),

  template({
    id: 'opposite-of',
    conceptId: 'absolute-value-opposites',
    description: 'Find the opposite of a number.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const magnitude = rng.int(2, difficulty === 1 ? 25 : 60);
      const start = rng.bool() ? magnitude : -magnitude;

      if (difficulty === 2 && rng.bool()) {
        return numericExercise({
          prompt: 'Evaluate.',
          latex: `-(${start})`,
          value: -start,
          summary: `The opposite of ${start} is ${-start}: same distance from zero, other side.`,
          hint: 'A minus sign in front asks for the opposite.',
          steps: [{ label: 'Reflect across zero.', latex: `-(${start}) = ${-start}` }],
          traps: [trap(start, 'opposite-sign-unchanged')],
        });
      }

      return numericExercise({
        prompt: `What is the opposite of ${start}?`,
        value: -start,
        summary: `${start} and ${-start} are the same distance from zero on opposite sides.`,
        hint: 'The opposite is the mirror image across zero.',
        steps: [
          { label: 'Keep the distance, change the side.', latex: `${start} \\rightarrow ${-start}` },
        ],
        traps: [trap(start, 'opposite-sign-unchanged')],
      });
    },
  }),

  template({
    id: 'absolute-value-compare',
    conceptId: 'absolute-value-opposites',
    description: 'Distinguish distance from zero from order on the number line.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      // The far one is deliberately the *smaller* number: that conflict is the
      // whole point of the question.
      const far = -rng.int(8, difficulty === 2 ? 20 : 45);
      const near = rng.bool() ? rng.int(1, 7) : -rng.int(1, 7);
      const askFurther = rng.bool();
      const answer = askFurther ? far : Math.max(far, near);

      return numericExercise({
        prompt: askFurther
          ? `Which of ${far} and ${near} is further from zero? Type that number.`
          : `Which of ${far} and ${near} is greater? Type that number.`,
        figure: {
          kind: 'number-line',
          from: Math.floor((far - 2) / 5) * 5,
          to: Math.ceil((Math.max(near, 0) + 2) / 5) * 5,
          step: 5,
          marks: [{ at: far }, { at: near }],
        },
        value: answer,
        summary: askFurther
          ? `|${far}| = ${-far} and |${near}| = ${Math.abs(near)}, so ${far} is further from zero — even though it is the smaller number.`
          : `${Math.max(far, near)} sits further right on the line, so it is greater — even though ${far} is further from zero.`,
        hint: askFurther ? 'Distance from zero ignores which side you are on.' : 'Greater means further right, not further from zero.',
        steps: [
          { label: 'Distances from zero.', latex: `|${far}| = ${-far}, \\quad |${near}| = ${Math.abs(near)}` },
          { label: 'Order on the line.', latex: `${far} < ${near}` },
          { label: askFurther ? 'Further from zero.' : 'Greater.', latex: String(answer) },
        ],
        traps: [trap(askFurther ? Math.max(far, near) : far, 'absolute-value-ordered-by-position')],
      });
    },
  }),

  /* ---------------------------------------------------------- inequalities */
  template({
    id: 'inequality-from-words',
    conceptId: 'one-variable-inequalities',
    description: 'Translate a condition in words into an inequality.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const bound = difficulty === 2 ? rng.int(3, 25) : rng.int(12, 90);
      const scenario = rng.pick([
        { text: `A lift holds at most ${bound} people. Write an inequality for the number of people p.`, variable: 'p', symbol: '\\le' },
        { text: `You must be at least ${bound} years old to enter. Write an inequality for the age a.`, variable: 'a', symbol: '\\ge' },
        { text: `The load must stay under ${bound} tonnes. Write an inequality for the load w.`, variable: 'w', symbol: '<' },
        { text: `More than ${bound} people came to the concert. Write an inequality for the number of people n.`, variable: 'n', symbol: '>' },
        { text: `Spend no more than ${bound} pounds. Write an inequality for the cost c.`, variable: 'c', symbol: '\\le' },
        { text: `The temperature stayed above ${bound} degrees all day. Write an inequality for the temperature t.`, variable: 't', symbol: '>' },
      ]);

      const { choices, correctId } = options(
        SYMBOLS.map((symbol) => ({
          latex: `${scenario.variable} ${symbol} ${bound}`,
          correct: symbol === scenario.symbol,
          ...(symbol === scenario.symbol
            ? {}
            : { misconceptionId: sameDirection(symbol, scenario.symbol) ? 'inequality-endpoint-included' : 'inequality-direction-misread' }),
        })),
        rng.shuffle,
      );

      return choiceExercise({
        prompt: scenario.text,
        choices,
        correctId,
        summary: `${describeSymbol(scenario.symbol)}, so the answer is ${scenario.variable} ${symbolText(scenario.symbol)} ${bound}.`,
        hint: 'Does the boundary value itself satisfy the condition? That decides between < and ≤.',
        steps: [
          { label: 'Decide which side the values are on.', latex: `${scenario.variable} ${strictOf(scenario.symbol)} ${bound}` },
          { label: 'Then decide whether the boundary is included.', latex: `${scenario.variable} ${scenario.symbol} ${bound}` },
        ],
      });
    },
  }),

  template({
    id: 'inequality-graph-read',
    conceptId: 'one-variable-inequalities',
    description: 'Read an inequality from its graph on the number line.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const at = difficulty === 4 ? rng.int(-6, -1) : rng.int(1, 7);
      const direction = rng.bool() ? 'right' : 'left';
      const closed = rng.bool();
      const symbol = direction === 'right' ? (closed ? '\\ge' : '>') : closed ? '\\le' : '<';

      const { choices, correctId } = options(
        SYMBOLS.map((candidate) => ({
          latex: `x ${candidate} ${at}`,
          correct: candidate === symbol,
          ...(candidate === symbol
            ? {}
            : { misconceptionId: sameDirection(candidate, symbol) ? 'inequality-endpoint-included' : 'inequality-direction-misread' }),
        })),
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which inequality does this graph show?',
        figure: { kind: 'number-line', from: at - 6, to: at + 6, step: 2, ray: { at, direction, closed } },
        choices,
        correctId,
        summary: `The shading goes ${direction === 'right' ? 'right' : 'left'}, and the circle is ${closed ? 'filled, so the endpoint is included' : 'hollow, so the endpoint is left out'}.`,
        hint: 'A filled circle includes the endpoint; a hollow one excludes it.',
        steps: [
          { label: `The shading runs to the ${direction}.`, latex: `x ${strictOf(symbol)} ${at}` },
          { label: closed ? 'The circle is filled, so the endpoint counts.' : 'The circle is hollow, so the endpoint does not count.', latex: `x ${symbol} ${at}` },
        ],
      });
    },
  }),

  template({
    id: 'inequality-whole-number-boundary',
    conceptId: 'one-variable-inequalities',
    description: 'Find the largest or smallest whole number satisfying an inequality.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const bound = difficulty === 3 ? rng.int(3, 20) : rng.int(20, 80);
      const symbol = rng.pick(SYMBOLS);
      const wantsSmallest = symbol === '>' || symbol === '\\ge';
      const answer = symbol === '>' ? bound + 1 : symbol === '\\ge' ? bound : symbol === '<' ? bound - 1 : bound;
      const wrong = symbol === '>' ? bound : symbol === '\\ge' ? bound + 1 : symbol === '<' ? bound : bound - 1;

      return numericExercise({
        prompt: `What is the ${wantsSmallest ? 'smallest' : 'largest'} whole number that satisfies this?`,
        latex: `x ${symbol} ${bound}`,
        value: answer,
        summary: `${symbolIncludes(symbol) ? `${bound} itself is allowed` : `${bound} itself is not allowed`}, so the answer is ${answer}.`,
        hint: `Test ${bound} in the inequality. Does it hold?`,
        steps: [
          { label: `Substitute ${bound}.`, latex: `${bound} ${symbol} ${bound} \\text{ is ${symbolIncludes(symbol) ? 'true' : 'false'}}` },
          { label: symbolIncludes(symbol) ? 'So the boundary itself is the answer.' : 'So step one past the boundary.', latex: String(answer) },
        ],
        traps: [trap(wrong, 'inequality-endpoint-included')],
      });
    },
  }),

  /* ------------------------------------------- relationships between two --*/
  template({
    id: 'relationship-evaluate',
    conceptId: 'two-variable-relationships',
    description: 'Use a rule relating two quantities to find a value.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const rate = rng.int(3, 12);
      const input = rng.int(3, difficulty === 2 ? 9 : 15);
      const start = difficulty === 2 ? 0 : rng.int(2, 20);
      const scenario = rng.pick([
        { rule: 'v', driver: 't', story: `A tap adds ${rate} litres a minute`, driverName: 'minutes', ruleName: 'litres' },
        { rule: 'd', driver: 'h', story: `A cyclist covers ${rate} kilometres an hour`, driverName: 'hours', ruleName: 'kilometres' },
        { rule: 'c', driver: 'n', story: `Tickets cost ${rate} pounds each`, driverName: 'tickets', ruleName: 'pounds' },
      ]);
      const answer = rate * input + start;
      const equation = start === 0 ? `${scenario.rule} = ${rate}${scenario.driver}` : `${scenario.rule} = ${rate}${scenario.driver} + ${start}`;

      return numericExercise({
        prompt: `${scenario.story}${start === 0 ? '' : `, and there ${scenario.rule === 'c' ? 'is a booking fee of' : 'is already a starting amount of'} ${start}`}. The rule is ${equation}. What is ${scenario.rule} when ${scenario.driver} = ${input}?`,
        value: answer,
        summary: `${rate} × ${input}${start === 0 ? '' : ` + ${start}`} = ${answer}.`,
        hint: `Substitute ${input} for ${scenario.driver}, then work out the value.`,
        steps: [
          { label: `Substitute ${scenario.driver} = ${input}.`, latex: `${scenario.rule} = ${rate} \\times ${input}${start === 0 ? '' : ` + ${start}`}` },
          { label: 'Multiply first.', latex: String(rate * input) },
          ...(start === 0 ? [] : [{ label: 'Then add the fixed part.', latex: `${rate * input} + ${start} = ${answer}` }]),
        ],
        traps: [
          trap(rate + input, 'comparison-read-as-additive'),
          ...(start === 0 ? [] : [trap(rate * input, 'intermediate-answer-given')]),
        ],
      });
    },
  }),

  template({
    id: 'relationship-equation',
    conceptId: 'two-variable-relationships',
    description: 'Choose the equation that matches a described relationship.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rate = rng.int(2, 15);
      const scenario = rng.pick([
        { story: `Apples cost ${rate} pounds each. Which equation gives the cost c of a apples?`, out: 'c', driver: 'a' },
        { story: `A machine fills ${rate} bottles a minute. Which equation gives the number of bottles b after m minutes?`, out: 'b', driver: 'm' },
        { story: `Each box holds ${rate} eggs. Which equation gives the number of eggs e in n boxes?`, out: 'e', driver: 'n' },
      ]);

      const { choices, correctId } = options(
        [
          { latex: `${scenario.out} = ${rate}${scenario.driver}`, correct: true },
          { latex: `${scenario.driver} = ${rate}${scenario.out}`, misconceptionId: 'dependent-independent-swapped' },
          { latex: `${scenario.out} = ${scenario.driver} + ${rate}`, misconceptionId: 'comparison-read-as-additive' },
          { latex: `${scenario.driver} = ${scenario.out} + ${rate}`, misconceptionId: 'dependent-independent-swapped' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: scenario.story,
        choices,
        correctId,
        summary: `Each unit of ${scenario.driver} contributes ${rate}, and ${scenario.out} is the quantity that follows, so ${scenario.out} = ${rate}${scenario.driver}.`,
        hint: 'Which quantity do you choose, and which one answers?',
        steps: [
          { label: 'The chosen quantity is the independent one.', latex: scenario.driver },
          { label: 'The one that follows goes on its own on the left.', latex: `${scenario.out} = ${rate}${scenario.driver}` },
        ],
      });
    },
  }),

  template({
    id: 'relationship-graph-point',
    conceptId: 'two-variable-relationships',
    description: 'Read a point on the graph of a relationship in the right order.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const rate = rng.int(2, 3);
      const marked = rng.int(2, difficulty === 3 ? 4 : 6);
      const height = rate * marked;
      const points = [1, 2, 3, 4, 5, 6].map((x) => ({ x, y: rate * x }));

      return numericExercise({
        prompt: `The graph shows the cost of apples, with apples across and cost up. The marked point is (${marked}, ${height}). How many apples does it stand for?`,
        figure: {
          kind: 'coordinate-grid',
          min: 0,
          max: rate * 6,
          points: points.map((point) => (point.x === marked ? { ...point, label: `(${marked}, ${height})` } : point)),
        },
        value: marked,
        summary: `A point is read across first, then up: ${marked} apples costing ${height}.`,
        hint: 'The first number belongs to the horizontal axis.',
        steps: [
          { label: 'The first coordinate is the horizontal one.', latex: String(marked) },
          { label: 'The second is the vertical one — the cost.', latex: String(height) },
        ],
        traps: [trap(height, 'coordinates-read-in-reverse')],
      });
    },
  }),

  /* ------------------------------------------------------------ histograms */
  template({
    id: 'histogram-read',
    conceptId: 'histograms',
    description: 'Read a frequency, a group of frequencies or a total from a histogram.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const width = rng.pick([5, 10, 20]);
      const counts = Array.from({ length: 5 }, () => rng.int(1, 15));
      const bins = counts.map((count, index) => ({ label: `${index * width}–${(index + 1) * width - 1}`, count }));
      const total = counts.reduce((sum, count) => sum + count, 0);
      const unitLabel = rng.pick(['minutes spent travelling', 'pages read in a day', 'grams of sugar per drink']);

      if (difficulty === 2) {
        const index = rng.int(0, 4);
        const bin = bins[index] as { label: string; count: number };
        return numericExercise({
          prompt: `How many observations fall in the interval ${bin.label}?`,
          figure: { kind: 'histogram', bins, unitLabel },
          value: bin.count,
          summary: `The bar over ${bin.label} reaches ${bin.count}, and that height is a count of observations.`,
          hint: 'Read the height of that one bar.',
          steps: [{ label: 'Read the bar height.', latex: String(bin.count) }],
          traps: [trap(index * width, 'histogram-bar-as-value')],
        });
      }

      if (difficulty === 3) {
        const cut = rng.int(2, 4);
        const below = counts.slice(0, cut).reduce((sum, count) => sum + count, 0);
        return numericExercise({
          prompt: `How many observations are below ${cut * width}?`,
          figure: { kind: 'histogram', bins, unitLabel },
          value: below,
          summary: `Adding the first ${cut} bars gives ${counts.slice(0, cut).join(' + ')} = ${below}.`,
          hint: 'Add the heights of every bar to the left of that value.',
          steps: [
            { label: `That covers the first ${cut} intervals.`, latex: counts.slice(0, cut).join(' + ') },
            { label: 'Add the heights.', latex: String(below) },
          ],
          traps: [trap(cut, 'histogram-bins-counted')],
        });
      }

      return numericExercise({
        prompt: 'How many observations are in the whole data set?',
        figure: { kind: 'histogram', bins, unitLabel },
        value: total,
        summary: `Every observation sits in exactly one interval, so the total is ${counts.join(' + ')} = ${total}.`,
        hint: 'Every observation is in one bar, so add all the heights.',
        steps: [
          { label: 'Add every bar.', latex: counts.join(' + ') },
          { label: 'Total.', latex: String(total) },
        ],
        traps: [trap(5, 'histogram-bins-counted')],
      });
    },
  }),

  template({
    id: 'statistical-question',
    conceptId: 'histograms',
    description: 'Tell a statistical question from one with a single answer.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const statistical = rng.pick([
        'How tall are the students in this school?',
        'How long do people in my street take to get to work?',
        'How many hours of sleep do children in Year 7 get?',
        'How much do the books in this library weigh?',
        'How old are the trees in this park?',
      ]);
      const single = rng.shuffle([
        'How tall am I?',
        'How many days are there in September?',
        'What time does this train leave?',
        'How many pupils are in my class today?',
        'What is the capital of France?',
      ]).slice(0, 3);

      const { choices, correctId } = options(
        [
          { label: statistical, correct: true },
          ...single.map((label) => ({ label, misconceptionId: 'statistical-question-single-answer' })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is a statistical question?',
        choices,
        correctId,
        summary: `"${statistical}" expects different answers from different people, and that variability is what makes it statistical.`,
        hint: 'Would you expect the answers to vary, or is there one answer that settles it?',
        steps: [
          { label: 'A statistical question anticipates variability.', latex: '\\text{many answers}' },
          { label: 'A question with one answer needs no data set.', latex: '\\text{one answer}' },
        ],
      });
    },
  }),

  /* ----------------------------------------------------------- variability */
  template({
    id: 'range-and-iqr',
    conceptId: 'data-variability',
    description: 'Find the range or the interquartile range of a data set.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const sorted: number[] = [];
      let value = rng.int(2, 15);
      for (let i = 0; i < 7; i += 1) {
        sorted.push(value);
        value += rng.int(1, difficulty === 2 ? 3 : 6);
      }
      const shown = rng.shuffle(sorted);
      const low = sorted[0] as number;
      const high = sorted[6] as number;
      const q1 = sorted[1] as number;
      const q3 = sorted[5] as number;
      const askRange = difficulty === 2 || rng.bool();

      if (askRange) {
        return numericExercise({
          prompt: `Find the range of: ${shown.join(', ')}`,
          value: high - low,
          summary: `${high} − ${low} = ${high - low}. The range is a width, not a value in the data.`,
          hint: 'Largest minus smallest.',
          steps: [
            { label: 'Sort, then take the two ends.', latex: `${low} \\text{ and } ${high}` },
            { label: 'Subtract.', latex: `${high} - ${low} = ${high - low}` },
          ],
          traps: [trap(high, 'range-as-largest-value')],
        });
      }

      return numericExercise({
        prompt: `Find the interquartile range of: ${shown.join(', ')}`,
        value: q3 - q1,
        summary: `In order the median is ${sorted[3]}, with ${q1} and ${q3} the quartiles, so the middle half is ${q3} − ${q1} = ${q3 - q1} wide.`,
        hint: 'Sort, find the median, then find the median of each half.',
        steps: [
          { label: 'Sort the values.', latex: sorted.join(', \\; ') },
          { label: 'The median splits them in half.', latex: String(sorted[3]) },
          { label: 'The quartiles are the medians of the halves.', latex: `Q_1 = ${q1}, \\quad Q_3 = ${q3}` },
          { label: 'Subtract.', latex: `${q3} - ${q1} = ${q3 - q1}` },
        ],
        traps: [trap(high - low, 'iqr-as-full-range')],
      });
    },
  }),

  template({
    id: 'mad-compute',
    conceptId: 'data-variability',
    description: 'Compute the mean absolute deviation of a small data set.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const mean = rng.int(12, difficulty === 3 ? 20 : 40);
      const spread = difficulty === 3 ? 4 : 6;
      /*
       * The first deviation is forced away from zero. Five identical values
       * would give a mean absolute deviation of 0 — a legitimate answer, but
       * one that coincides with the "deviations cancelled out" trap, so the
       * question could no longer tell the two apart.
       */
      const first = [
        rng.int(1, spread) * (rng.bool() ? 1 : -1),
        ...Array.from({ length: 3 }, () => rng.int(-spread, spread)),
      ];
      const last = -first.reduce((sum, deviation) => sum + deviation, 0);
      const deviations = [...first, last];
      const values = deviations.map((deviation) => mean + deviation);
      const totalDistance = deviations.reduce((sum, deviation) => sum + Math.abs(deviation), 0);
      const mad = roundTo(totalDistance / 5, 2);

      return numericExercise({
        prompt: `The mean of ${values.join(', ')} is ${mean}. What is the mean absolute deviation?`,
        value: mad,
        summary: `The distances from ${mean} are ${deviations.map((deviation) => Math.abs(deviation)).join(', ')}, and their mean is ${mad}.`,
        hint: 'Take each distance from the mean as a positive amount, then average those distances.',
        steps: [
          { label: `Distance of each value from ${mean}.`, latex: deviations.map((deviation) => Math.abs(deviation)).join(', \\; ') },
          { label: 'Add the distances.', latex: String(totalDistance) },
          { label: 'Divide by how many values there are.', latex: `${totalDistance} \\div 5 = ${mad}` },
        ],
        traps: [
          trap(0, 'deviations-not-absolute'),
          trap(totalDistance, 'mean-wrong-divisor'),
        ],
      });
    },
  }),

  template({
    id: 'box-plot-read',
    conceptId: 'data-variability',
    description: 'Read spread from a box plot.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const min = rng.int(1, 10);
      const q1 = min + rng.int(2, 6);
      const median = q1 + rng.int(2, 6);
      const q3 = median + rng.int(2, 6);
      const max = q3 + rng.int(2, 6);
      const askIqr = difficulty === 3 || rng.bool();
      const label = rng.pick(['test scores', 'daily rainfall in mm', 'minutes spent waiting']);

      return numericExercise({
        prompt: askIqr ? 'What is the interquartile range?' : 'What is the range?',
        figure: { kind: 'box-plot', min, q1, median, q3, max, from: 0, to: max + 3, label },
        value: askIqr ? q3 - q1 : max - min,
        summary: askIqr
          ? `The box runs from ${q1} to ${q3}, so the middle half is ${q3 - q1} wide.`
          : `The whiskers reach ${min} and ${max}, so the data spans ${max - min}.`,
        hint: askIqr ? 'The interquartile range is the width of the box.' : 'The range stretches from whisker to whisker.',
        steps: [
          { label: askIqr ? 'Read the two ends of the box.' : 'Read the two ends of the whiskers.', latex: askIqr ? `${q1} \\text{ and } ${q3}` : `${min} \\text{ and } ${max}` },
          { label: 'Subtract.', latex: askIqr ? `${q3} - ${q1} = ${q3 - q1}` : `${max} - ${min} = ${max - min}` },
        ],
        traps: askIqr ? [trap(max - min, 'iqr-as-full-range')] : [trap(max, 'range-as-largest-value')],
      });
    },
  }),
];
